/**
 * Miscellaneous GraphQL queries
 * Diagnosis causes, insurance options, filters, and other utility queries
 */

export const MISC_QUERIES = {
  // Diagnosis and Insurance queries
  GET_DIAGNOSIS_CAUSES: `
    query GetDiagnosisCauses {
      api_prescriptioncauselist(where: {active: {_eq: true}}, order_by: {id: asc}) {
        id
        cause
      }
    }
  `,

  // Primary Diagnosis Options query
  GET_PRIMARY_DX_OPTIONS: `
    query GetPrimaryDxOptions($clinicId: Int!, $facilityIds: [Int!]!, $providerIds: [Int!]!) {
      pep_vw_consolidated_data(
        where: {
          clinic_id: {_eq: $clinicId},
          facility_id: {_in: $facilityIds},
          provider_id: {_in: $providerIds}
        },
        distinct_on: primary_dx
      ) {
        primary_dx
      }
    }
  `,

  // Secondary Diagnosis Options query (dependent on Primary Dx)
  GET_SECONDARY_DX_OPTIONS: `
    query GetSecondaryDxOptions($clinicId: Int!, $primaryList: [String!]!) {
      pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          clinic_id: {_eq: $clinicId},
          primary_dx: {_in: $primaryList}
        },
        distinct_on: secondary_dx
      ) {
        secondary_dx
      }
    }
  `,

  // Primary Insurance Options query (dependent on Primary Dx)
  GET_PRIMARY_INSURANCE_OPTIONS: `
    query GetPrimaryInsuranceOptions($clinicId: Int!, $primaryList: [String!]!) {
      pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          clinic_id: {_eq: $clinicId},
          primary_dx: {_in: $primaryList}
        },
        distinct_on: primary_insurance_provider
      ) {
        primary_insurance_provider
      }
    }
  `,

  // Combined Filter Options query (dependent on Primary and Secondary Dx)
  GET_COMBINED_FILTER_OPTIONS: `
    query GetCombinedFilterOptions($clinicId: Int!, $primaryList: [String!]!, $secondaryList: [String!]!) {
      primary_insurance: pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          clinic_id: {_eq: $clinicId},
          primary_dx: {_in: $primaryList},
          secondary_dx: {_in: $secondaryList}
        },
        distinct_on: primary_insurance_provider
      ) {
        primary_insurance_provider
      }
      secondary_insurance: pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          clinic_id: {_eq: $clinicId},
          primary_dx: {_in: $primaryList},
          secondary_dx: {_in: $secondaryList}
        },
        distinct_on: secondary_insurance_provider
      ) {
        secondary_insurance_provider
      }
      insurance_type: pep_vw_consolidated_data(
        where: {
          ccm_status: {_nin: [1,2,3,7,8,9,10]},
          clinic_id: {_eq: $clinicId},
          primary_dx: {_in: $primaryList},
          secondary_dx: {_in: $secondaryList}
        },
        distinct_on: insurance_type
      ) {
        insurance_type
      }
    }
  `,

  // Insurance Dependent Options query
  GET_INSURANCE_DEPENDENT_OPTIONS: `
    query GetInsuranceDependentOptions(
      $secondaryInsuranceWhere: pep_vw_consolidated_data_bool_exp!,
      $insuranceTypeWhere: pep_vw_consolidated_data_bool_exp!
    ) {
      secondary_insurance: pep_vw_consolidated_data(
        where: $secondaryInsuranceWhere,
        distinct_on: secondary_insurance_provider
      ) {
        secondary_insurance_provider
      }
      insurance_type: pep_vw_consolidated_data(
        where: $insuranceTypeWhere,
        distinct_on: insurance_type
      ) {
        insurance_type
      }
    }
  `,

  // From GRAPHQL_QUERIES - Primary Dx list
  PRIMARY_DX_LIST: `
    query MyQuery {
      pep_vw_consolidated_data(distinct_on: primary_dx) {
        primary_dx
      }
    }
  `,

  // Secondary Dx list (from GRAPHQL_QUERIES)
  SECONDARY_DX_LIST: `
    query MyQuery {
      pep_vw_consolidated_data(distinct_on: secondary_dx) {
        secondary_dx
      }
    }
  `,

  // Primary Insurance Provider (from GRAPHQL_QUERIES)
  PRIMARY_INSURANCE_PROVIDERS: `
    query MyQuery {
      pep_vw_consolidated_data(distinct_on: primary_insurance_provider) {
        primary_insurance_provider
      }
    }
  `,

  // Secondary Insurance Provider (from GRAPHQL_QUERIES)
  SECONDARY_INSURANCE_PROVIDERS: `
    query MyQuery {
      pep_vw_consolidated_data(distinct_on: secondary_insurance_provider) {
        secondary_insurance_provider
      }
    }
  `,

  // Apply Filters Simple (from GRAPHQL_QUERIES)
  APPLY_FILTERS_SIMPLE: `
    query ApplyFiltersSimple($clinicId: Int!) {
      pep_vw_consolidated_data(where: {ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicId}}) {
        patient_clinic_data_id
        has_active_group
      }
    }
  `,

  // Apply Filters Complex (from GRAPHQL_QUERIES)
  APPLY_FILTERS_COMPLEX: `
    query ApplyFiltersComplex($clinicId: Int!, $facilityIds: [Int!], $providerIds: [Int!], $primaryList: [String!], $secondaryList: [String!], $primaryInsList: [String!], $secondaryInsList: [String!], $insuranceType: [String!], $formattedDate: date) {
      pep_vw_consolidated_data(where: {ccm_status: {_nin: [1,2,3,7,8,9,10]}, clinic_id: {_eq: $clinicId}, facility_id: {_in: $facilityIds}, provider_id: {_in: $providerIds}}) {
        patient_clinic_data_id
        has_active_group
      }
    }
  `,

  // Get Diagnosis Causes (from GRAPHQL_QUERIES)
  GET_DIAGNOSIS_CAUSES_SIMPLE: `
    query GetDiagnosisCauses {
      api_prescriptioncauselist {
        id
        cause
      }
    }
  `,

  // Get all Prescription Cause List
  GET_PRESCRIPTION_CAUSE_LIST: `
    query GetPrescriptionCauseList {
      api_prescriptioncauselist {
        active
        cause
      }
    }
  `,

  // SMS Status Values query
  SMS_STATUS_VALUES: `
    query SMSStatusValues {
      api_sms(distinct_on: status, where: {status: {_neq: ""}}) {
        id
        status
      }
    }
  `,

  // SMS Opt-out check query
  SMS_OPT_OUT_CHECK: `
    query SMSOptOutCheck($patient_id: bigint!) {
      api_sms_opt_out(where: { patient_id: {_eq: $patient_id}, is_deleted: {_eq: false} }) {
        patient_id
        is_deleted
        phone_number
        reason
        key
        id
        created_at
        clinic_phone_number
        updated_at
      }
    }
  `,

  // Get insurance providers from emr_patient_insurance
  GET_INSURANCE_PROVIDERS: `
    query GetPrimaryInsuranceList {
      emr_patient_insurance(
        where: {coordination_of_benefits: {_eq: 1}}, 
        distinct_on: insurance_payor
      ) {
        insurance_payor
        insurance_id
      }
    }
  `,

  GET_STATES: `
    query GetStates {
      api_state {
        id
        state_name
        state_code
      }
    }
  `
};